// =============================================================================
// features/sales/campaigns/detail/tabs/PortfolioTab.tsx — Aba "Carteira":
// membros paginados, responsável, estado, tentativas e próxima ação
// (doc 26 §6.7/§4.2). Remoção com motivo obrigatório (F35-S04, LGPD §8.5).
//
// DoD do slot: "nenhuma tela carrega 10 mil membros de uma vez" — paginação
// real via `limit`/`offset` do servidor, nunca carregada inteira no cliente.
// `leadId` é o único identificador do lead disponível (a API de carteira
// NUNCA inclui nome/telefone — LGPD §8.5, ver members/schemas.ts).
// =============================================================================

import type { Campaign, CampaignMember, CampaignMemberStatus } from '@elemento/shared-schemas';
import * as React from 'react';

import type { ResponsiveTableColumn } from '../../../../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../../../../components/ui/ResponsiveTable';
import { Select } from '../../../../../components/ui/Select';
import { useToast } from '../../../../../components/ui/Toast';
import {
  useCampaignMembers,
  useRemoveCampaignMember,
} from '../../../../../hooks/sales/useCampaignDetail';
import { ApiError } from '../../../../../lib/api';
import { formatOrgDateTime } from '../../../../../lib/orgDateTime';
import { DetailPagination } from '../components/DetailPagination';
import { MemberStatusBadge } from '../components/MemberStatusBadge';
import { RemoveMemberDialog } from '../components/RemoveMemberDialog';

export interface PortfolioTabProps {
  campaign: Campaign;
  canManage: boolean;
}

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: CampaignMemberStatus | ''; label: string }[] = [
  { value: '', label: 'Todos os estados' },
  { value: 'pending', label: 'Pendente' },
  { value: 'available', label: 'Disponível' },
  { value: 'claimed', label: 'Em atendimento' },
  { value: 'waiting_callback', label: 'Aguardando retorno' },
  { value: 'completed', label: 'Concluído' },
  { value: 'blocked', label: 'Bloqueado' },
  { value: 'removed', label: 'Removido' },
];

function leadIdLabel(leadId: string): string {
  return `Lead ${leadId.slice(0, 8)}…`;
}

export function PortfolioTab({ campaign, canManage }: PortfolioTabProps): React.JSX.Element {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = React.useState<CampaignMemberStatus | ''>('');
  const [offset, setOffset] = React.useState(0);
  const [removeTarget, setRemoveTarget] = React.useState<CampaignMember | null>(null);

  const filters = React.useMemo(
    () => ({
      ...(statusFilter ? { status: [statusFilter] } : {}),
      limit: PAGE_SIZE,
      offset,
    }),
    [statusFilter, offset],
  );

  const { data, isLoading, isError, refetch } = useCampaignMembers(campaign.id, filters);
  const removeMutation = useRemoveCampaignMember(campaign.id);

  function handleFilterChange(next: CampaignMemberStatus | ''): void {
    setStatusFilter(next);
    setOffset(0);
  }

  async function handleConfirmRemove(
    reason: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    if (!removeTarget) return { ok: false, message: 'Nenhum membro selecionado.' };
    try {
      await removeMutation.mutateAsync({ memberId: removeTarget.id, reason });
      toast('Membro removido da carteira.', 'success');
      return { ok: true };
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível remover este membro agora.';
      return { ok: false, message };
    }
  }

  const columns: ResponsiveTableColumn<CampaignMember>[] = [
    {
      key: 'lead',
      header: 'Cliente',
      primary: true,
      cell: (m) => <span className="font-mono text-sm text-ink">{leadIdLabel(m.leadId)}</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (m) => <MemberStatusBadge status={m.status} />,
    },
    {
      key: 'attempts',
      header: 'Tentativas',
      align: 'right',
      hideBelow: 'md',
      cell: (m) => <span className="font-mono text-sm text-ink-2">{m.attemptCount}</span>,
    },
    {
      key: 'nextAction',
      header: 'Próxima ação',
      hideBelow: 'md',
      cell: (m) => (
        <span className="font-sans text-sm text-ink-2">
          {m.nextActionAt ? formatOrgDateTime(m.nextActionAt) : '—'}
        </span>
      ),
    },
    {
      key: 'assignee',
      header: 'Responsável',
      hideBelow: 'lg',
      cell: (m) => (
        <span className="font-sans text-sm text-ink-2">
          {m.assignedAgentId ? `${m.assignedAgentId.slice(0, 8)}…` : 'Fila compartilhada'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      hideInCard: !canManage,
      cell: (m) =>
        canManage && m.status !== 'removed' ? (
          <button
            type="button"
            onClick={() => setRemoveTarget(m)}
            className="font-sans text-xs font-medium text-danger hover:underline min-h-[32px] px-1"
          >
            Remover
          </button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select
          id="portfolio-status-filter"
          label="Estado"
          value={statusFilter}
          onChange={(e) => handleFilterChange(e.target.value as CampaignMemberStatus | '')}
          options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          wrapperClassName="w-56"
        />
      </div>

      <div
        className="rounded-md border border-border overflow-hidden"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      >
        {isError ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center px-4">
            <p className="font-sans text-sm text-ink-3">Não foi possível carregar a carteira.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="font-sans text-sm font-medium text-azul hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={data?.data ?? []}
              getRowKey={(m) => m.id}
              isLoading={isLoading}
              aria-label="Carteira da campanha"
              emptyState={
                <div className="flex flex-col items-center gap-2 py-12 text-center px-4">
                  <p className="font-sans text-sm text-ink-3">
                    {statusFilter
                      ? 'Nenhum membro com esse estado.'
                      : 'Nenhum membro na carteira ainda.'}
                  </p>
                </div>
              }
            />
            <DetailPagination
              offset={offset}
              limit={PAGE_SIZE}
              total={data?.total ?? 0}
              onOffsetChange={setOffset}
              itemLabel="membros"
            />
          </>
        )}
      </div>

      {removeTarget && (
        <RemoveMemberDialog
          leadIdLabel={leadIdLabel(removeTarget.leadId)}
          onClose={() => setRemoveTarget(null)}
          onConfirm={handleConfirmRemove}
        />
      )}
    </div>
  );
}
